import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, filter, map, of, switchMap, take } from 'rxjs';
import { CitySelectionDialogComponent } from '@shared/components/city-selection-dialog/city-selection-dialog.component';
import { UserPreferencesService } from './user-preferences.service';
import { AuthRepository } from '@core/auth/state/auth.repository';

@Injectable({
  providedIn: 'root'
})
export class CityDialogService {
  private dialog = inject(MatDialog);
  private userPrefs = inject(UserPreferencesService);
  private authRepo = inject(AuthRepository);
  
  /**
   * Check if we need to show the city selection dialog
   * and open it if necessary, but only if user is authenticated
   */
  checkAndShowCityDialog(): Observable<string | null> {
    return this.authRepo.token$.pipe(
      take(1),
      switchMap(token => {
        const isAuthenticated = !!token;
        
        // Don't show dialog if user isn't authenticated
        if (!isAuthenticated) {
          return of(null);
        }
        
        // If the user has already selected a city, don't show the dialog
        if (this.userPrefs.hasCity()) {
          return of(this.userPrefs.getCity());
        }
        
        // Set Moscow as default city instead of showing the dialog
        this.userPrefs.setCity('Москва');
        return of('Москва');
      })
    );
  }
  
  /**
   * Open the city selection dialog - only when manually triggered
   */
  openCityDialog(): Observable<string | null> {
    return this.authRepo.token$.pipe(
      take(1),
      map(token => !!token),
      switchMap(isAuthenticated => {
        // Check if user is authenticated before opening the dialog
        if (!isAuthenticated) {
          return of(null);
        }
        
        const dialogRef: MatDialogRef<CitySelectionDialogComponent> = this.dialog.open(
          CitySelectionDialogComponent,
          {
            width: '450px',
            disableClose: true,
            autoFocus: true,
            hasBackdrop: true
          }
        );
        
        return dialogRef.afterClosed().pipe(
          filter(result => !!result)
        );
      })
    );
  }
} 